import omit from "lodash/omit";
import { getActiveUser } from "@noom/hive-auth";
import { HealthConditionsProto } from "@noom/noom-contracts/noom_contracts/usermodel/baselined/health/shared";

import {
  addSurveyAnswers,
  SurveyAnswersState,
} from "@utils/redux/slices/surveyAnswers";
import {
  getSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import { captureException } from "src/utils/error";
import { send } from "src/utils/fetch";
import { updateUserData, UserDataState } from "src/utils/redux/slices/userData";
import getStore from "src/utils/redux/store";
import { getMainSurveyUserDataNamespace } from "src/utils/survey/surveyNamespace";
import { isShopContext } from "src/utils/userSegment/features";

// Plain save: no survey-submitted tracking or CRM event for a re-save.
const SAVE_USER_DATA = "/userdata/api/v4/user/data/save/";

type ShopSurveyAnswersBeforeLogin = {
  anonymousUserId?: string;
  surveyAnswers: JsonObject;
  healthConditions?: HealthConditionsProto;
};

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    shopSurveyAnswersBeforeLogin?: ShopSurveyAnswersBeforeLogin;
  }
}

/**
 * The logged-out survey is saved under the anonymous growth user, but after
 * login shop loads userData by access code, so the answers have to be carried
 * across the Identity Server round trip.
 */
export function saveShopSurveyAnswersBeforeLogin() {
  const { userData } = getStore().getState();
  const surveyAnswers = userData[getMainSurveyUserDataNamespace()] ?? {};
  updateSessionState("browser", {
    shopSurveyAnswersBeforeLogin: {
      anonymousUserId: userData.user_id,
      // The account's email is the one to keep.
      surveyAnswers: omit(surveyAnswers, "email") as JsonObject,
      healthConditions: userData.healthConditions,
    },
  });
}

export function clearShopSurveyAnswersBeforeLogin() {
  updateSessionState("browser", { shopSurveyAnswersBeforeLogin: undefined });
}

/** Answers from the survey just taken win over what the account already had. */
export function buildRestoredUserData(
  userData: UserDataState,
  saved: ShopSurveyAnswersBeforeLogin,
  surveyNameSpace: keyof UserDataState
): UserDataState {
  return {
    [surveyNameSpace]: {
      ...(userData[surveyNameSpace] as SurveyAnswersState),
      ...(saved.surveyAnswers as SurveyAnswersState),
    },
    ...(saved.healthConditions && {
      healthConditions: {
        ...userData.healthConditions,
        ...saved.healthConditions,
      },
    }),
  };
}

/**
 * Puts the answers into userData for this page load and saves them to the
 * account. They stay in the session until the save succeeds, so a failed save
 * is retried on the next load.
 */
export function restoreShopSurveyAnswersAfterLogin() {
  const saved = getSessionState("browser").shopSurveyAnswersBeforeLogin as
    | ShopSurveyAnswersBeforeLogin
    | undefined;
  if (!saved || !isShopContext() || !getActiveUser()?.accessCode) {
    return;
  }

  const store = getStore();
  const surveyNameSpace = getMainSurveyUserDataNamespace();
  const restored = buildRestoredUserData(
    store.getState().userData,
    saved,
    surveyNameSpace
  );
  store.dispatch(updateUserData(restored));
  // The account's older answers also went into the surveyAnswers slice, which
  // checkout reads before userData for some fields, e.g. date of birth.
  store.dispatch(addSurveyAnswers(saved.surveyAnswers as SurveyAnswersState));

  // Loading the account switched the `_userId` cookie, which the backend saves
  // to, from the anonymous user to the account's user.
  const accountUserId = store.getState().userData.user_id;
  if (!accountUserId) {
    return;
  }
  if (accountUserId === saved.anonymousUserId) {
    // The survey already saved the answers to this user.
    clearShopSurveyAnswersBeforeLogin();
    return;
  }

  send("POST", SAVE_USER_DATA, {
    ...restored,
    surveyNameSpace,
    clientUserId: accountUserId,
    lastUpdated: Date.now(),
  })
    .then(clearShopSurveyAnswersBeforeLogin)
    .catch(captureException);
}
